<template>
  <v-layout column>
    <input
      type="file"
      style="display: none"
      ref="selectedDocuments"
      multiple
      @change="onDocumentsPicked"
    />

    <v-layout column align-center v-if="checklistLoading">
      <v-progress-circular
        indeterminate
        size="40"
        class="ma-5"
        color="primary"
      ></v-progress-circular>
    </v-layout>

    <v-layout column v-else class="mb-2 subheading mt-2">
      <h2 class="section-title text-xs-left mt-2 mb-3">Uploaded documents</h2>

      <v-layout class="mb-2" wrap>
        <v-btn
          block
          @click="pickDocuments"
          class="primary hidden-sm-and-up text-none mb-3"
          :loading="documentUploading"
        >
          Choose Files or Take Picture
        </v-btn>

        <v-flex xs12 sm7>
          <v-layout column v-if="isEmpty(currentQuestionnaireOtherDocuments)">
            <span class="text-xs-left"
              >No documents have been uploaded yet.</span
            >
          </v-layout>

          <v-layout
            v-else
            align-center
            class="file-item mb-2 text-xs-left body-1"
            :key="i"
            v-for="(document, i) in currentQuestionnaireOtherDocuments"
          >
            <v-icon color="primary" class="mr-1 material-icons-outlined">
              insert_drive_file
            </v-icon>
            {{ document.name }}
            <v-spacer />
            <v-icon
              @click="deleteExtractedDocument(document.id)"
              style="cursor: pointer"
              color="error darken-1"
              class="ml-1"
              size="20"
              >remove_circle</v-icon
            >
          </v-layout>
        </v-flex>

        <v-flex
          sm5
          @drop.prevent="addFile"
          @dragover.prevent
          class="hidden-xs-only"
        >
          <v-layout class="drag-and-drop ml-3" style="min-height: 200px">
            <v-layout
              column
              align-center
              justify-center
              v-if="documentUploading"
            >
              <v-progress-circular
                indeterminate
                size="30"
                color="primary"
              ></v-progress-circular>
            </v-layout>
            <v-layout column align-center justify-center v-else>
              <div>
                <v-icon>upload_file</v-icon>
              </div>
              <div>
                <span @click="pickDocuments" class="drag-and-drop-text"
                  >Drag and drop files here or
                  <span class="clickable primary--text">browse</span></span
                >
              </div>
              <span class="max-size-text">(Max Size 150MB)</span>
            </v-layout>
          </v-layout>
        </v-flex>
      </v-layout>

      <v-divider class="my-3" />

      <h2 class="section-title text-xs-left mt-1 mb-2" v-if="!hideList">
        Document checklist
      </h2>

      <span
        v-if="isEmpty(allDocumentsNeeded) && !hideList"
        class="darkgrey--text body-2 text-xs-left"
        >Documents were not requested for the questionnaire.</span
      >

      <v-layout
        v-if="!hideList"
        v-for="question in allDocumentsNeeded"
        :key="question.id"
        row
        justify-center
        align-center
        class="mb-1 document-item"
        :class="
          isEmpty(question.answer) && showUnanswered && !question.optional
            ? 'error--text outlined-red my-2 pa-2'
            : ''
        "
      >
        <v-flex
          class="text-xs-left"
          xs7
          sm7
          :class="$vuetify.breakpoint.xsOnly ? 'caption' : 'body-1'"
        >
          <v-layout align-center class="">
            <v-layout row>
              <div class="pr-2">
                <v-icon
                  class="icon"
                  v-if="!isEmpty(question.answer)"
                  color="green"
                  >done</v-icon
                >
                <span v-else class="icon dot">•</span>
              </div>
              <span class="document-item-text">{{
                !isEmpty(question.options)
                  ? question.options.join(', ')
                  : question.text
              }}</span>
            </v-layout>

            <template>
              <v-tooltip bottom v-if="question.uploaded_last_year">
                <v-icon class="primary--text" slot="activator" size="18"
                  >help</v-icon
                >
                <span>You uploaded this last year</span>
              </v-tooltip>
              <v-tooltip bottom v-if="question.follow_up_question_id">
                <v-icon class="primary--text" slot="activator" size="18"
                  >help</v-icon
                >
                <span
                  v-html="
                    formatReason(
                      question,
                      getParentQuestion(findSection(question), question),
                    )
                  "
                />
              </v-tooltip>
              <v-tooltip bottom v-if="filteredComments(question).length">
                <v-chip
                  slot="activator"
                  class="clickable-chip"
                  small
                  @click="$emit('open-comment-dialog', question)"
                  >{{ filteredComments(question).length }} Note{{
                    filteredComments(question).length > 1 ? 's' : ''
                  }}</v-chip
                >
                <span>Click to view notes</span>
              </v-tooltip>
            </template>
          </v-layout>
        </v-flex>

        <v-flex xs6 sm5 class="pl-2">
          <v-select
            class="default-input select-option ml-2"
            :class="$vuetify.breakpoint.xsOnly ? 'caption' : 'body-1'"
            solo
            hide-details
            :value="question.answer"
            placeholder="Choose an option"
            :items="filteredUploadOptions(question)"
            clearable
            :item-text="$vuetify.breakpoint.xsOnly ? 'mobile_text' : 'text'"
            item-value="value"
            @change="saveQuestion(question, $event)"
          />
        </v-flex>
      </v-layout>
    </v-layout>
  </v-layout>
</template>

<script>
import isEmpty from 'lodash/isEmpty';
import { mapGetters } from 'vuex';
import API from '@/api';
import moment from 'moment';
import FirmPreferenceMixins from '@/mixins/FirmPreferenceMixins';
import NewDocumentChecklistMixin from '@/mixins/NewDocumentChecklistMixin';
import QuestionnaireMixin from '@/mixins/QuestionnaireMixin';

export default {
  props: {
    checklistLoading: {
      type: Boolean,
    },

    currentQuestionnaire: {
      type: Object,
      required: true,
    },

    selectedQuestion: {
      type: Object,
    },

    asyncForEach: {
      type: Function,
    },

    showUnanswered: {
      type: Boolean,
      default: false,
    },

    hideList: {
      optional: true,
      type: Boolean,
      default: false,
    },
  },

  mixins: [FirmPreferenceMixins, NewDocumentChecklistMixin, QuestionnaireMixin],

  data() {
    return {
      uploadOptions: [
        {
          text: 'Provided Above',
          mobile_text: 'Provided Above',
          value: 'Provided in Questionnaire',
        },
        {
          text: 'Provided Elsewhere (e.g. Email)',
          mobile_text: 'Provided Elsewhere',
          value: 'Provided Elsewhere',
        },
        {
          text: 'Upload Later',
          mobile_text: 'Upload Later',
          value: 'Upload Later',
        },
        {
          text: 'Not Applicable',
          mobile_text: 'Not Applicable',
          value: 'Not Applicable',
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'user',
    }),
    currentQuestionnaireOtherDocuments() {
      if (this.currentQuestionnaire.extracted_documents) {
        return this.currentQuestionnaire.extracted_documents.filter(d =>
          d.source.toLowerCase().includes(`/bulk uploads/`),
        );
      }
    },
    areAllDocumentsNamed() {
      return this.documentsToImport.every(d => !isEmpty(d.name));
    },
  },

  methods: {
    isEmpty,

    moment,

    filteredUploadOptions(question) {
      let filters = [];
      if (question.disable_answer_later) {
        filters.push('Upload Later');
      }
      if (question.disable_not_applicable) {
        filters.push('Not Applicable');
      }
      if (this.disableProvidedElsewhere) {
        filters.push('Provided Elsewhere');
      }
      return this.uploadOptions.filter(a => !filters.includes(a.value));
    },

    uploadLaterDate(question) {
      if (question.answer_by) {
        return ` (${question.answer_by})`;
      } else {
        return '';
      }
    },

    listDocumentsWording(question) {
      return `${question.answer
        .split('&!&')
        .slice(1)
        .map(a => a.split('/')[a.split('/').length - 1])
        .join(', ')}`;
    },

    openRecognizedDocument(question) {
      this.$emit('preview-document', this.recognizedDocument(question));
    },

    recognizedDocument(question) {
      return this.currentQuestionnaire.extracted_documents.find(
        q =>
          q.detailed_name &&
          question.options
            .map(o => o.toLowerCase())
            .includes(q.detailed_name.toLowerCase()),
      );
    },

    listDocuments(answer) {
      return answer
        .split('&!&')
        .filter((item, index) => !(index === 0 && item === ''));
    },

    formatReason(question, parentQuestion) {
      return `${this.currentUser.is_advisor ? 'Client' : 'You'} answered '${
        question.trigger_for_follow_up_question
      }' to<br/>'${parentQuestion.text}'`;
    },

    async deleteExtractedDocument(id) {
      try {
        const response = await API.extractedDocument.deleteExtractedDocument(
          id,
        );
        if (response.data.error) {
          console.log(response.data.error);
        } else {
          this.$store.dispatch('application/setSuccessNotification', {
            message: 'Successfully deleted.',
          });
          this.currentQuestionnaire.extracted_documents = this.currentQuestionnaire.extracted_documents.filter(
            d => d.id !== id,
          );
        }
      } catch (e) {
        console.log(e);
      }
    },
    pickDocuments() {
      const input = this.$refs.selectedDocuments;
      input.type = 'text';
      input.type = 'file';
      this.$refs.selectedDocuments.click();
    },

    async addFile(e) {
      const documents = e.dataTransfer.files;
      if (documents) {
        const documentType = this.selectedDocumentType;
        this.documentsToImport.push(
          ...Array.from(documents).map(document => {
            return {
              name: this.getNameWithoutExtensionOfFilename(document.name),
              data: document,
              extension: this.getExtensionOfFilename(document.name),
            };
          }),
        );
        this.uploadBulkDocument();
      }
    },

    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },

    async onDocumentsPicked(e) {
      const documents = e.target.files;
      if (documents) {
        this.documentsToImport.push(
          ...Array.from(documents).map(document => {
            return {
              name: this.getNameWithoutExtensionOfFilename(document.name),
              data: document,
              extension: this.getExtensionOfFilename(document.name),
            };
          }),
        );
        this.uploadBulkDocument();
      }
    },
  },
};
</script>

<style lang="postcss" scoped>
.section-title {
  font-weight: 700;
  font-size: 20px;
}
.file-item {
  padding: 10px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  background: #fff;
}
.document-item {
  padding: 5px 0px;
}
.icon {
  text-align: center;
  display: inline-block;
  width: 24px;
  height: 24px;
}
.dot {
  color: #aaa;
  font-size: 14px;
}
.document-item-text {
  font-size: 16px;
}
.select-option {
  & >>> .v-input__control .v-input__slot {
    min-height: 46px;
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-raidus: 4px;
    font-size: 15px;
  }
}
.why-text {
  font-size: 12px;
}
.action-button {
  & >>> .v-btn__content {
    white-space: normal;
  }
}
.drag-and-drop {
  height: 140px;
  border-radius: 4px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.2);

  & >>> i {
    color: rgba(0, 0, 0, 0.3);
  }

  & >>> span {
    color: rgba(0, 0, 0, 0.6);
  }

  & >>> span.clickable {
    cursor: pointer;
  }
}
.clickable-chip {
  & >>> .v-chip__content {
    cursor: pointer !important;
  }
}
.outlined-red {
  border: 1px solid #cb4039;
}
.drag-and-drop-text {
  font-size: 14px;
}
.max-size-text {
  font-size: 14px;
}
</style>
